import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import { ToastContainer } from "react-toastify";
import { BrowserRouter } from "react-router-dom";
import ColorModeContext from "./layout/themeContext";
import { ChatProvider } from './context/Chat/ChatContext';
import { exposeCacheUtils } from './utils/cacheUtils';

import Routes from "./routes";
import { createAppTheme } from "./theme";
import { AuthProvider } from "./context/Auth/AuthContext";
import { StaticResourcesContextProvider } from "./context/StaticResources/StaticResourcesContext";
import ChatWidget from "./components/ChatWidget";
import { CloudPaymentRequiredDialogHost } from "./components/CloudPaymentRequiredDialog";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60,
      cacheTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const App = () => {
    const [locale, setLocale] = useState();
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createAppTheme(mode, locale);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);

    // 🛠️ Exponer utilidades de cache en desarrollo
    useEffect(() => {
        exposeCacheUtils();
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <StaticResourcesContextProvider>
                    <ChatProvider>
                        <ColorModeContext.Provider value={{ colorMode }}>
                            <ThemeProvider theme={theme}>
                                <QueryClientProvider client={queryClient}>
                                    <Routes />
                                    <CloudPaymentRequiredDialogHost />
                                </QueryClientProvider>
                            </ThemeProvider>
                        </ColorModeContext.Provider>
                    </ChatProvider>
                </StaticResourcesContextProvider>
            </AuthProvider>
        </BrowserRouter>
    );
};

export default App;